import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  ArrowLeft, Home, Building2, Plus, CheckCircle2, Ban, UserPlus, Receipt, Pencil, MailCheck, ShieldCheck,
} from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Badge } from "src/components/ui/badge"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import {
  useBooking, useCancelBooking, useCompleteBooking, useProvisionCustomer, useSendEmailVerification,
} from "src/features/bookings/hooks/useBookings"
import { RecordPaymentDialog } from "src/features/bookings/components/RecordPaymentDialog"
import { CustomerEditDrawer } from "src/features/bookings/components/CustomerEditDrawer"
import { NotesPanel } from "src/features/notes/NotesPanel"
import { TasksPanel } from "src/features/tasks/components/TasksPanel"
import { TagEntitySection } from "src/features/tags/components/TagEntitySection"
import { DocumentList } from "src/features/documents/components/DocumentList"
import { BookingChecklist } from "src/features/documents/components/BookingChecklist"
import QuotationButton from "src/features/documents/components/QuotationButton"
import BookingConfirmationButton from "src/features/bookings/components/BookingConfirmationButton"

const BOOKING_DOCUMENT_CATEGORIES = ["booking", "sales_allotment", "agreement", "kyc", "receipt", "completion"]
import {
  formatMoney, formatDate, BOOKING_STATUS_BADGE, BOOKING_STATUS_LABEL,
  INSTALLMENT_STATUS_STYLE,
} from "src/features/bookings/constants"

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  )
}

export default function BookingDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { booking, loading, reload } = useBooking(id)
  const cancelBooking = useCancelBooking()
  const completeBooking = useCompleteBooking()
  const provision = useProvisionCustomer()
  const sendVerification = useSendEmailVerification()

  const [payTarget, setPayTarget] = useState(undefined) // undefined=closed, null=ad-hoc, obj=installment
  const [editCustomer, setEditCustomer] = useState(false)
  const [docRefresh, setDocRefresh] = useState(0) // bump to re-sync the checklist after doc changes

  async function run(fn, args, okTitle) {
    try {
      await fn.mutate(...args)
      toast({ title: okTitle })
      reload()
    } catch (e) {
      toast({ variant: "destructive", title: "Action failed", description: Array.isArray(e) ? e[0] : "" })
    }
  }

  if (loading && !booking) {
    return <AppShell><div className="px-8 py-7"><Skeleton className="h-40 w-full max-w-2xl rounded-2xl" /></div></AppShell>
  }
  if (!booking) {
    return <AppShell><div className="px-8 py-7 text-sm text-slate-400">Booking not found.</div></AppShell>
  }

  const isActive = ["reserved", "confirmed"].includes(booking.status)
  const schedule = booking.schedule || []
  const payments = booking.payments || []

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">
        <button onClick={() => navigate("/bookings")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back to bookings
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <Home className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-xl font-semibold tracking-tight text-slate-900">{booking.customer?.name || "Customer"}</h1>
                <Badge variant={BOOKING_STATUS_BADGE[booking.status] || "secondary"}>
                  {BOOKING_STATUS_LABEL[booking.status] || booking.status}
                </Badge>
              </div>
              <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-500">
                <Building2 className="h-3.5 w-3.5" />
                {booking.project?.name || "—"} · Unit {booking.unit?.unit_number || "—"} · Booked {formatDate(booking.booking_date)}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPayTarget(null)} disabled={!isActive}>
              <Plus className="h-4 w-4" /> Record payment
            </Button>
            <QuotationButton bookingId={booking.id} onGenerated={() => setDocRefresh((n) => n + 1)} />
            <BookingConfirmationButton bookingId={booking.id} onGenerated={() => setDocRefresh((n) => n + 1)} />
            {!booking.customer_user_id && !booking.customer?.email_verified && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => run(sendVerification, [booking.id], "Verification email sent")} disabled={sendVerification.isPending || !booking.customer?.email}>
                <MailCheck className="h-4 w-4" />
                {booking.customer?.email_verification_sent ? "Resend verification" : "Send verification email"}
              </Button>
            )}
            {!booking.customer_user_id && booking.customer?.email_verified && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => run(provision, [booking.id], "Customer portal access granted")} disabled={provision.isPending}>
                <UserPlus className="h-4 w-4" /> Grant portal access
              </Button>
            )}
            {isActive && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => run(completeBooking, [booking.id], "Booking completed")} disabled={completeBooking.isPending}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Complete
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  const reason = window.prompt("Reason for cancelling? (optional)") ?? undefined
                  run(cancelBooking, [booking.id, reason], "Booking cancelled")
                }} disabled={cancelBooking.isPending}>
                  <Ban className="h-4 w-4 text-red-500" /> Cancel
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tags */}
        <TagEntitySection
          taggableType="Booking"
          taggableId={booking.id}
          initialTags={booking.tags || []}
          variant="inline"
          onChanged={reload}
        />

        {/* Money summary */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {booking.original_price > 0 && (
            <StatCard label="Unit price (list)" value={formatMoney(booking.original_price)} accent="text-slate-400" />
          )}
          <StatCard label="Agreement value" value={formatMoney(booking.total_value)} />
          <StatCard label="Booking amount" value={formatMoney(booking.booking_amount)} />
          <StatCard label="Collected" value={formatMoney(booking.amount_received)} accent="text-emerald-600" />
          <StatCard label="Balance due" value={formatMoney(booking.balance_due)} accent="text-amber-600" />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
          {/* Payment schedule */}
          <div className="rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-3 text-sm font-medium text-slate-700">Payment schedule</div>
            {schedule.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">No payment schedule for this booking.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {schedule.map((inst) => {
                  const style = INSTALLMENT_STATUS_STYLE[inst.status] || INSTALLMENT_STATUS_STYLE.due
                  const paid = inst.status === "paid"
                  return (
                    <div key={inst.id} className="flex items-center justify-between gap-3 px-5 py-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">{inst.name}</div>
                        <div className="text-[11px] text-slate-400">
                          Due {formatDate(inst.due_on)}
                          {inst.amount_paid > 0 && inst.balance > 0 ? ` · ${formatMoney(inst.amount_paid)} paid` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-700">{formatMoney(inst.amount)}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${style.cls}`}>{style.label}</span>
                        {!paid && isActive && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => setPayTarget(inst)}>
                            Record
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Customer + payments */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs text-slate-400">Customer</span>
                <button
                  onClick={() => setEditCustomer(true)}
                  className="flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-800"
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
              </div>
              <dl className="space-y-2 text-sm">
                <Field k="Name" v={booking.customer?.name} />
                <Field k="Phone" v={booking.customer?.phone} />
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-[11px] text-slate-400">Email</dt>
                  <dd className="flex items-center gap-1 truncate text-slate-700">
                    {booking.customer?.email || "—"}
                    {booking.customer?.email_verified ? (
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500" title="Verified" />
                    ) : booking.customer?.email ? (
                      <span className="shrink-0 rounded bg-amber-100 px-1 text-[9px] font-semibold uppercase text-amber-700">Unverified</span>
                    ) : null}
                  </dd>
                </div>
                <Field k="Deal owner" v={booking.owner?.full_name} />
                {booking.channel_partner && (
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <dt className="text-[11px] text-slate-400">Channel Partner</dt>
                    <dd className="truncate text-slate-700">{booking.channel_partner.name} ({booking.channel_partner.referral_code || "no code"})</dd>
                  </div>
                )}
                <Field k="Portal access" v={booking.customer_user_id ? "Granted" : "Not granted"} />
              </dl>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700">
                <Receipt className="h-4 w-4 text-slate-400" /> Receipts
              </div>
              {payments.length === 0 ? (
                <div className="px-4 py-6 text-center text-[13px] text-slate-400">No payments recorded yet.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div>
                        <div className="font-medium text-slate-800">{formatMoney(p.amount)}</div>
                        <div className="text-[11px] uppercase text-slate-400">{p.mode} · {formatDate(p.paid_on)}</div>
                      </div>
                      {p.reference_no && <span className="text-[11px] text-slate-400">{p.reference_no}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <TasksPanel parentType="bookings" parentId={id} />
            <BookingChecklist bookingId={id} refreshKey={docRefresh} />
            <DocumentList
              scope={{ type: "booking", id }}
              categories={BOOKING_DOCUMENT_CATEGORIES}
              refreshKey={docRefresh}
              onChange={() => setDocRefresh((n) => n + 1)}
            />
            <NotesPanel notable="bookings" id={id} />
          </div>
        </div>
      </div>

      <RecordPaymentDialog
        open={payTarget !== undefined}
        booking={booking}
        installment={payTarget || null}
        onClose={() => setPayTarget(undefined)}
        onSuccess={reload}
      />

      <CustomerEditDrawer
        open={editCustomer}
        booking={booking}
        onClose={() => setEditCustomer(false)}
        onSuccess={reload}
      />
    </AppShell>
  )
}

function Field({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-[11px] text-slate-400">{k}</dt>
      <dd className="truncate text-slate-700">{v || "—"}</dd>
    </div>
  )
}
